import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNextTrip } from '../services/plannerApi';

/**
 * Contextual landing (spec §22.5, Phase 4): open on **Now** during a trip or
 * within seven days of departure, and on **Explore** otherwise.
 *
 * Two constraints shape this, and both are easy to get wrong:
 *
 * 1. **It must fire only on entry, not on every visit to `/`.** The Now tab
 *    points at `/`. A redirect that ran whenever the path was `/` would make Now
 *    unreachable — tap it, get bounced to Explore, forever. So the redirect runs
 *    at most once per browser session and only from the entry URL.
 *
 * 2. **Route paths stay stable** (spec §22.5). This selects which tab opens; it
 *    does not rename or remove a route. `/` still renders Now for anyone who
 *    navigates there deliberately, including a bookmark or a shared link.
 *
 * The decision itself is the server's (`GET /api/planner/next-trip/` →
 * `landing`), not this hook's. Three clients previously each decided "within
 * seven days" for themselves and their day-difference helpers disagreed about
 * timezones; the rule now lives in `planner/next_trip.py` and the client obeys it.
 *
 * 3. **A redirect never happens after Now has been painted.** Rendering Now and
 *    then switching to Explore a moment later read as a bug. So while the answer
 *    for an entry at `/` is outstanding, the hook reports `holding` and the app
 *    renders its neutral loading state in place of Now — for at most
 *    `LANDING_HOLD_CAP_MS`. An answer inside the cap opens straight on the right
 *    tab; a failure, or the cap firing first, renders Now and closes the landing
 *    for that entry, so a late answer does nothing.
 */

/**
 * The longest Now is held back waiting for the landing answer. Owner decision
 * (about one second, the same cap as mobile's splash hold); past it Now renders
 * and stays.
 */
export const LANDING_HOLD_CAP_MS = 1000;

/**
 * Keyed by ACCOUNT, not just by browser session. One global key meant that once
 * account A had landed, account B signing in during the same session got no
 * contextual landing at all.
 */
function sessionKey(userId: number | null | undefined): string {
  return `vizday:landing-applied:${userId ?? 'anon'}`;
}

/**
 * Routes that are not a destination in their own right, so must not be recorded as
 * where the session began. Mirrors `GlobalHeader`'s hidden paths and mobile's
 * `TRANSIENT_ROUTES`.
 */
const TRANSIENT_ROUTES = ['/login', '/signup', '/reset-password', '/verify-email'];

function isTransientRoute(pathname: string): boolean {
  return TRANSIENT_ROUTES.some((r) => pathname.startsWith(r));
}

type LandingAnswer = {
  landing: 'now' | 'explore' | undefined;
  failed: boolean;
};

/**
 * Asks the server where this account should land.
 *
 * The answer is stored WITH the account it was fetched for, and compared at render.
 * `setLanding(undefined)` in an effect was not enough: on a user change the render
 * that follows still holds account A's answer, and the landing hook's effects run
 * from that same render — so A's `explore` could redirect B, or A's `now` could
 * consume B's marker and lose B's legitimate landing.
 *
 * Keyed on the ACCOUNT, not a boolean. Depending only on "signed in" meant a direct
 * account replacement — no unauthenticated render in between — never refetched.
 */
export function useNextTripLanding(userId: number | null): LandingAnswer {
  const [answer, setAnswer] = useState<{
    userId: number;
    landing?: 'now' | 'explore';
    failed?: boolean;
  } | null>(null);

  useEffect(() => {
    if (userId === null) {
      setAnswer(null);
      return;
    }
    let cancelled = false;
    getNextTrip()
      .then((r) => {
        if (!cancelled) setAnswer({ userId, landing: r.landing });
      })
      // A failed lookup must not redirect anywhere: no landing is invented. It is
      // reported, though, so a held Now renders at once instead of at the cap.
      .catch(() => {
        if (!cancelled) setAnswer({ userId, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const mine = userId !== null && answer?.userId === userId ? answer : null;
  return { landing: mine?.landing, failed: !!mine?.failed };
}

/**
 * One account's landing in this mount. Tagged with the account and compared at
 * render, so the render on which the account changes already reads a fresh state —
 * the hold below is computed during render, and a reset that ran in an effect a
 * frame later would let the previous account's state decide whether Now paints.
 */
type LandingState = {
  userId: number | null | undefined;
  /** Where the user was when landing first became *applicable*, captured once. */
  entry: string | null;
  /** Guards against a second run inside one mount. */
  applied: boolean;
  /** The user went somewhere themselves; any later `/` is a deliberate Now tap. */
  expired: boolean;
  /** Now has been shown for this entry (the cap fired): never redirect it. */
  closed: boolean;
};

function freshState(userId: number | null | undefined): LandingState {
  return { userId, entry: null, applied: false, expired: false, closed: false };
}

export function useContextualLanding(
  landing: 'now' | 'explore' | undefined,
  {
    enabled = true,
    userId,
    failed = false,
  }: { enabled?: boolean; userId?: number | null; failed?: boolean } = {}
): { holding: boolean } {
  const SESSION_KEY = sessionKey(userId);
  const navigate = useNavigate();
  const location = useLocation();
  const stateRef = useRef<LandingState>(freshState(userId));
  // A new account gets its own decision: entry, expiry and the cap reset with it.
  // Derived at render (see `LandingState`), then made durable by the effects.
  const state = stateRef.current.userId === userId ? stateRef.current : freshState(userId);
  // The cap closes the entry from a timer; this re-renders so Now replaces the hold.
  const [, setCapTick] = useState(0);

  // The entry, as the effect below WILL capture it. Read at render so the very
  // first render at `/` is already held — the effect runs after the browser may
  // have painted.
  //
  // Not the mount path: this hook is disabled while signed out, so a session that
  // begins at `/login` and lands on `/` after authenticating would otherwise record
  // `/login` as its entry and never land at all. Captured when `enabled` first
  // turns true instead, which is the moment the question starts to make sense.
  //
  // And not the current path either — testing that alone let a user who entered on
  // a shared link keep an unconsumed one-shot, so their first deliberate tap on Now
  // bounced them to Explore.
  //
  // Not while passing through an auth screen. Sign-in sets the user in the store
  // before the route changes, so `enabled` can flip while the location is still
  // `/login` — capturing there records a non-Now entry, and the following redirect
  // to `/` then expires the opportunity.
  const entry = state.entry
    ?? (enabled && !isTransientRoute(location.pathname) ? location.pathname : null);

  // Whether this entry may still redirect: the same conditions the redirect effect
  // checks, evaluated now.
  let open = enabled
    && entry === '/'
    && location.pathname === '/'
    && !state.applied
    && !state.expired
    && !state.closed;
  if (open) {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') open = false;
    } catch {
      // Unreadable storage never redirects (see below), so there is nothing to wait for.
      open = false;
    }
  }
  // Held while the answer is outstanding, and through an `explore` answer until the
  // redirect effect has navigated — rendering Now for that one commit would be the
  // very flash this exists to remove.
  const holding = open && (landing === undefined ? !failed : landing === 'explore');

  useEffect(() => {
    if (stateRef.current.userId !== userId) stateRef.current = freshState(userId);
  }, [userId]);

  useEffect(() => {
    if (!holding) return;
    const timer = setTimeout(() => {
      const current = stateRef.current;
      if (current.userId !== userId) return;
      // Closed in memory only: the user has not landed, so a reload — a new entry,
      // held again — may still land. Within this mount the entry is over.
      current.closed = true;
      setCapTick((n) => n + 1);
    }, LANDING_HOLD_CAP_MS);
    return () => clearTimeout(timer);
  }, [holding, userId]);

  useEffect(() => {
    if (!enabled) return;
    if (stateRef.current.userId !== userId) stateRef.current = freshState(userId);
    const current = stateRef.current;
    if (current.entry === null) {
      if (!isTransientRoute(location.pathname)) {
        current.entry = location.pathname;
      }
      return;
    }
    if (location.pathname !== current.entry) current.expired = true;
  }, [enabled, location.pathname, userId]);

  useEffect(() => {
    if (!enabled || !landing) return;
    if (stateRef.current.userId !== userId) stateRef.current = freshState(userId);
    const current = stateRef.current;
    // The opportunity expires as soon as the user goes somewhere themselves, and
    // the entry is closed once Now has been shown — including the case where a
    // slow server answer lands after either.
    if (current.applied || current.expired || current.closed) return;
    // Landing applies from `/` only, and only while we are still there.
    if (current.entry !== '/') return;
    if (location.pathname !== '/') return;

    let alreadyApplied = false;
    try {
      alreadyApplied = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      // Private browsing can throw on access. Treating that as "not applied"
      // would redirect on every navigation to `/`, which is the one outcome
      // that breaks the Now tab — so fail closed and never redirect.
      return;
    }
    if (alreadyApplied) return;

    current.applied = true;
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* best effort — the ref still prevents a loop within this mount */
    }

    if (landing === 'explore') {
      // `replace` so Back does not bounce between `/` and `/explore`.
      navigate('/explore', { replace: true });
    }
  }, [enabled, landing, location.pathname, navigate, SESSION_KEY, userId]);

  return { holding };
}

/** Test seam: forget that landing was applied for this session. */
export function resetContextualLanding(userId?: number | null) {
  try {
    sessionStorage.removeItem(sessionKey(userId));
  } catch {
    /* nothing to reset */
  }
}
